/**
 * Login — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Presentation migrated to `@/design-system` primitives + `--ds-*` tokens.
 * Every load-bearing behaviour is preserved verbatim:
 *   • `login()` call + resolveRedirect + navigate replace
 *   • Test IDs (`login-form-*`, `login-toggle-password`, etc.)
 *   • Error state via <Alert tone="danger">
 *   • Copy: calm advisory ("Sign in to your workspace")
 */
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { resolveRedirect } from "@/auth/ProtectedRoute";
import { formatApiError } from "@/api/client";
import AuthShell from "./AuthShell";
import { Button, Input, Alert } from "@/design-system";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Email and password are required."); return; }
    setSubmitting(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      const fallback = user.funding_profile_complete ? "/dashboard" : "/profile";
      const { pathname: nextPath, search: mergedSearch } = resolveRedirect(
        nextParam,
        location.search,
      );
      const dest = nextPath || fallback;
      navigate(
        { pathname: dest, search: mergedSearch, hash: location.hash },
        { replace: true },
      );
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      testId="login-page"
      title="Sign in to your workspace."
      subtitle="Enter the email and password associated with your account."
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormField label="Email" htmlFor="login-form-email">
          <Input
            id="login-form-email"
            data-testid="login-form-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Password" htmlFor="login-form-password">
          <div className="relative">
            <Input
              id="login-form-password"
              data-testid="login-form-password"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              data-testid="login-toggle-password"
              aria-label={show ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-2 flex items-center justify-center px-2 text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] rounded-[var(--ds-radius-sm)]"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>
        {error && (
          <Alert tone="danger" data-testid="login-form-error">
            {error}
          </Alert>
        )}
        <Button
          type="submit"
          data-testid="login-form-submit"
          loading={submitting}
          disabled={submitting}
          className="w-full"
          size="lg"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
        <div className="flex items-center justify-between text-[13px] pt-1">
          <Link
            to="/forgot-password"
            data-testid="login-forgot-password"
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-accent)] transition-colors"
          >
            Forgot password?
          </Link>
          <Link
            to="/signup"
            data-testid="login-to-signup-link"
            className="text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors font-medium"
          >
            Create an account
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

/**
 * Small helper — pairs a caption-style field label with the wrapped
 * <Input>. Local to the auth-cluster pages (Login/Signup/Reset) so
 * they share a consistent label look without pulling in extra deps.
 */
function FormField({ label, htmlFor, children }) {
  return (
    <label className="block" htmlFor={htmlFor}>
      <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

export { FormField };
